import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'
import Switch from '../../../components/Switch'
import { useWebPush } from '../../../customHooks/useWebPush'

function reasonMessage(trans, reason) {
  switch (reason) {
    case 'unsupported':
      return trans({ id: 'notifications_web_push_unsupported', defaultMessage: 'Browser push is not supported in this environment. Use HTTPS or localhost.' })
    case 'denied':
      return trans({ id: 'notifications_web_push_permission_denied', defaultMessage: 'Notifications are blocked. Allow them in your browser site settings and try again.' })
    case 'config_error':
      return trans({ id: 'notifications_web_push_config_error', defaultMessage: 'Push is not configured. Contact your administrator.' })
    default:
      return trans({ id: 'notifications_web_push_enable_error', defaultMessage: 'Could not enable browser notifications.' })
  }
}

export default function WebPushToggle({ compact = false }) {
  const { formatMessage: trans } = useIntl()
  const { supported, permission, enabled, busy, toggle } = useWebPush()

  const handleChange = useCallback(
    async (checked) => {
      const result = await toggle(checked)
      if (checked && result && !result.ok) {
        Swal.fire({ icon: 'warning', title: trans({ id: 'notifications_web_push_title', defaultMessage: 'Browser notifications' }), text: reasonMessage(trans, result.reason) })
      }
    },
    [toggle, trans],
  )

  if (!supported && permission !== 'granted') {
    return compact ? null : <p className="small text-muted mb-0">{trans({ id: 'notifications_web_push_unsupported', defaultMessage: 'Browser push is not supported in this environment. Use HTTPS or localhost.' })}</p>
  }

  const label = enabled ? trans({ id: 'notifications_web_push_enabled', defaultMessage: 'Browser notifications on' }) : trans({ id: 'notifications_web_push_disabled', defaultMessage: 'Browser notifications off' })

  if (compact) {
    return (
      <div className="header-notifications-web-push px-3 py-2 border-top">
        <Switch
          size="small"
          label={trans({ id: 'notifications_web_push_title', defaultMessage: 'Browser notifications' })}
          checked={enabled}
          onChange={handleChange}
          disabled={busy || (!enabled && permission === 'denied')}
        />
        {!enabled && permission === 'denied' ? (
          <p className="font-small text-muted mb-0 mt-1">
            {trans({ id: 'notifications_web_push_permission_denied', defaultMessage: 'Notifications are blocked. Allow them in your browser site settings and try again.' })}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="notifications-web-push-card border rounded-3 p-3 mb-3 bg-white">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <div className="fw-semibold fs-12">{trans({ id: 'notifications_web_push_title', defaultMessage: 'Browser notifications' })}</div>
          <p className="fs-11 text-muted mb-0">{trans({ id: 'notifications_web_push_description', defaultMessage: 'With this tab active, new pushes update the bell inbox only (no popup). When the tab is in the background or closed, your OS shows a banner; sound and other alert options are configured in your system or browser.' })}</p>
        </div>
        <Switch label={label} checked={enabled} onChange={handleChange} disabled={busy || (!enabled && permission === 'denied')} />
      </div>
      {!enabled && permission === 'denied' ? (
        <p className="font-small text-danger mb-0 mt-2">
          {trans({ id: 'notifications_web_push_permission_denied', defaultMessage: 'Notifications are blocked. Allow them in your browser site settings and try again.' })}
        </p>
      ) : null}
    </div>
  )
}
